import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase/firebaseConfig";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { getUserProfile } from "../services/userService";

const AdminLogin = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const ADMIN_EMAIL = "admin@bookplatform.com";
    const ADMIN_PASSWORD = "AdminPassword123!";

    useEffect(() => {
        // Redirect if already logged in as admin
        const checkSession = auth.onAuthStateChanged(async (user) => {
            if (user) {
                if (user.email === ADMIN_EMAIL) {
                    navigate("/admin/dashboard");
                } else {
                    const profile = await getUserProfile(user.uid);
                    if (profile && profile.role === "admin") {
                        navigate("/admin/dashboard");
                    }
                }
            }
        });
        return () => checkSession();
    }, [navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");

        try {
            // Check for default admin auto-provisioning
            if (email.trim().toLowerCase() === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
                let user;
                try {
                    // Try signing in
                    const userCredential = await signInWithEmailAndPassword(auth, ADMIN_EMAIL, ADMIN_PASSWORD);
                    user = userCredential.user;
                } catch (signInErr) {
                    // If account doesn't exist, create it (auto-provisioning)
                    if (signInErr.code === "auth/user-not-found" || signInErr.code === "auth/invalid-credential") {
                        try {
                            const userCredential = await createUserWithEmailAndPassword(auth, ADMIN_EMAIL, ADMIN_PASSWORD);
                            user = userCredential.user;
                        } catch (createErr) {
                            throw new Error("Auto-provisioning failed: " + createErr.message);
                        }
                    } else {
                        throw signInErr;
                    }
                }

                // Ensure admin user document is registered in Firestore
                if (user) {
                    await setDoc(doc(db, "users", user.uid), {
                        name: "System Administrator",
                        email: ADMIN_EMAIL,
                        role: "admin",
                        createdAt: serverTimestamp(),
                    }, { merge: true });
                    navigate("/admin/dashboard");
                    return;
                }
            }

            // Normal login flow for other admins
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            const profile = await getUserProfile(user.uid);
            if (profile && profile.role === "admin") {
                navigate("/admin/dashboard");
            } else {
                // Not an admin account, sign out
                await auth.signOut();
                setError("Access Denied: You do not have administrator permissions.");
            }
        } catch (err) {
            console.error("Admin login error:", err);
            setError(err.message.replace("Firebase: ", "").replace(/\(auth.*\)\.?/, ""));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 px-4 relative overflow-hidden font-sans">
            {/* Glowing decorative circles */}
            <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-rose-500/10 rounded-full blur-[100px] pointer-events-none"></div>
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none"></div>

            <div className="w-full max-w-md bg-slate-900/60 backdrop-blur-xl border border-slate-800 rounded-2xl shadow-2xl p-8 relative z-10">
                <div className="flex flex-col items-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-tr from-rose-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20 mb-4 transform hover:rotate-12 transition-transform duration-300">
                        <span className="text-3xl">🛡️</span>
                    </div>
                    <h1 className="text-2xl font-bold text-white tracking-tight">Admin Console</h1>
                    <p className="text-slate-400 text-sm mt-1">Book Creation Platform</p>
                </div>

                {error && (
                    <div className="bg-rose-500/10 border border-rose-500/30 text-rose-400 text-sm rounded-lg p-3.5 mb-6 flex items-start gap-2.5">
                        <span className="text-base mt-0.5">⚠️</span>
                        <span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleLogin} className="space-y-5">
                    <div>
                        <label className="block text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">
                            Admin Login ID
                        </label>
                        <input
                            type="email"
                            placeholder="admin@bookplatform.com"
                            className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all duration-200"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">
                            Password
                        </label>
                        <input
                            type="password"
                            placeholder="••••••••"
                            className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all duration-200"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-rose-500 to-indigo-600 hover:from-rose-600 hover:to-indigo-700 text-white font-semibold py-3 px-4 rounded-lg shadow-lg hover:shadow-indigo-500/20 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:pointer-events-none mt-2"
                    >
                        {loading ? (
                            <span className="flex items-center justify-center gap-2">
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                Entering Console...
                            </span>
                        ) : (
                            "Sign In to Console"
                        )}
                    </button>
                </form>

                <div className="mt-8 pt-6 border-t border-slate-800/60 text-center">
                    <button
                        onClick={() => navigate("/")}
                        className="text-indigo-400 hover:text-indigo-300 text-xs font-medium transition-colors"
                    >
                        ← Back to Author Landing
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;
